<template>
	<div class="landing" ref="landing-home-page">
		<span class="header-background" :class="{ 'v-hidden': isV3Active }" />
		<div class="hero-section" :class="{ 'hero-section--v3': isV3Active }">
			<GlobalHero v-if="!isV3Active" />
		</div>

		<template v-if="isV3Active">
			<div class="v3-bg">
				<img :src="v3BgSrc" alt="" aria-hidden="true" />
			</div>
			<StartPageHeroRotator class="landing__rotator" />
		</template>

		<!-- wrapper keeps .landing's child list stable through hydration -->
		<div class="landing__sr">
			<slot v-if="isMounted" name="sponsoredRecommendation" />
		</div>

		<GlobalTitles
			v-show="!isV1Active || jwtvModulesLoading"
			v-if="globalTitles?.MAIN_TOP_10_MOVIES?.edges?.length"
			class="landing__featured"
			:country="country"
			:language="language"
			:objectType="ObjectType.Movie"
			type="TOP_10"
			module-name="MAIN_TOP_10_FREE_MOVIES"
			:titles="globalTitles?.MAIN_TOP_10_MOVIES?.edges ?? []"
			featured
		/>
		<GlobalTitles
			v-if="isV1Active && jwtvModules?.MAIN_TOP_10_MOVIES?.edges?.length"
			class="landing__featured"
			:country="country"
			:language="language"
			:objectType="ObjectType.Movie"
			type="TOP_10"
			module-name="MAIN_TOP_10_FREE_MOVIES"
			:titles="jwtvModules?.MAIN_TOP_10_MOVIES?.edges ?? []"
			featured
		>
			<template #heading>
				<h2 class="global-titles__card__content__heading">Top 10 movies for free</h2>
			</template>
			<template #text>
				<p class="global-titles__card__content__text">
					Check out this week’s most popular movies and find out where to watch them for free.
				</p>
			</template>
		</GlobalTitles>

		<!-- <LazyHydrate when-idle> -->
		<GlobalTitles
			v-show="!isV1Active || jwtvModulesLoading"
			v-if="globalTitles?.MAIN_TOP_10_SHOWS?.edges?.length"
			class="landing__featured"
			type="TOP_10"
			:objectType="ObjectType.Show"
			:language="language"
			:country="country"
			module-name="MAIN_TOP_10_SHOWS"
			:titles="globalTitles?.MAIN_TOP_10_SHOWS?.edges ?? []"
			featured
		/>
		<GlobalTitles
			v-if="isV1Active && jwtvModules?.MAIN_TOP_10_SHOWS?.edges?.length"
			class="landing__featured"
			type="TOP_10"
			:objectType="ObjectType.Show"
			:language="language"
			:country="country"
			module-name="MAIN_TOP_10_SHOWS"
			:titles="jwtvModules?.MAIN_TOP_10_SHOWS?.edges ?? []"
			featured
		>
			<template #heading>
				<h2 class="global-titles__card__content__heading">Top 10 TV shows for free</h2>
			</template>
			<template #text>
				<p class="global-titles__card__content__text">
					Check out this week’s most popular TV shows and find out where to watch them for free.
				</p>
			</template>
		</GlobalTitles>
		<!-- </LazyHydrate> -->

		<LazyGlobalTitles
			v-if="globalTitles?.MAIN_JWTV_TVOD?.edges?.length"
			class="landing__featured"
			:country="country"
			:language="language"
			type="TOP_10"
			module-name="MAIN_JWTV_TVOD"
			:titles="globalTitles?.MAIN_JWTV_TVOD?.edges ?? []"
			featured
		/>

		<div class="landing__heading--intro">
			<h2>{{ $t('WEBAPP_HOW_JUSTWATCH_WORKS') }}</h2>
		</div>

		<LazyGlobalCards :country="country" :language="language" />

		<LazyGlobalLargeSpotlight
			v-if="globalTitles?.MAIN_LARGE_SPOTLIGHT?.edges?.length"
			:language="language"
			:country="country"
			module-name="MAIN_LARGE_SPOTLIGHT"
			:titles="globalTitles?.MAIN_LARGE_SPOTLIGHT?.edges ?? []"
		/>

		<LazyGlobalTitlesWhenVisible
			v-if="globalTitles?.MAIN_JWTV_AVOD?.edges?.length"
			:country="country"
			:language="language"
			type="POPULAR"
			module-name="MAIN_JWTV_AVOD"
			:titles="globalTitles?.MAIN_JWTV_AVOD?.edges ?? []"
		/>

		<LazyGlobalTitlesWhenVisible
			v-if="globalTitles?.MAIN_NEW_MOVIES?.edges?.length"
			type="NEW"
			:objectType="ObjectType.Movie"
			:language="language"
			:country="country"
			module-name="MAIN_NEW_MOVIES"
			:titles="globalTitles?.MAIN_NEW_MOVIES?.edges ?? []"
		/>

		<LazyGlobalTitlesWhenVisible
			v-if="globalTitles?.MAIN_TOP_10_GENRE_COMEDY?.edges?.length"
			type="TOP_10"
			genre="cmy"
			:language="language"
			:country="country"
			module-name="MAIN_TOP_10_GENRE_COMEDY"
			:titles="globalTitles?.MAIN_TOP_10_GENRE_COMEDY?.edges ?? []"
		/>

		<LazyGlobalTitlesWhenVisible
			v-if="globalTitles?.MAIN_TOP_10_GENRE_DRAMA?.edges?.length"
			type="TOP_10"
			genre="drm"
			:language="language"
			:country="country"
			module-name="MAIN_TOP_10_GENRE_DRAMA"
			:titles="globalTitles?.MAIN_TOP_10_GENRE_DRAMA?.edges ?? []"
		/>

		<LazyGlobalTitlesWhenVisible
			v-if="globalTitles?.MAIN_TOP_10_GENRE_HORROR?.edges?.length"
			type="TOP_10"
			genre="hrr"
			:language="language"
			:country="country"
			module-name="MAIN_TOP_10_GENRE_HORROR"
			:titles="globalTitles?.MAIN_TOP_10_GENRE_HORROR?.edges ?? []"
		/>

		<div class="landing__heading--sub">
			<h2>{{ $t('WEBAPP_GLOBAL_NEW_SHOW') }}</h2>
		</div>

		<LazyGlobalSmallSpotlight
			v-if="globalTitles?.MAIN_SMALL_SPOTLIGHT?.edges?.length"
			:language="language"
			:country="country"
			module-name="MAIN_SMALL_SPOTLIGHT"
			:titles="globalTitles?.MAIN_SMALL_SPOTLIGHT?.edges ?? []"
		/>

		<LazyGlobalTitlesWhenVisible
			v-if="globalTitles?.MAIN_NEW_SHOWS?.edges?.length"
			type="NEW"
			:country="country"
			:objectType="ObjectType.Show"
			:language="language"
			module-name="MAIN_NEW_SHOWS"
			:titles="globalTitles?.MAIN_NEW_SHOWS?.edges ?? []"
		/>

		<LazyGlobalProviderSpotlight
			v-if="!!providersByShortName[spotlightProvider.shortName]"
			:country="country"
			:language="language"
			:provider="spotlightProvider"
			module-name="MAIN_TOP_PROVIDER"
			:titles="globalTitles?.MAIN_TOP_PROVIDER?.edges ?? []"
		/>

		<LazyGlobalTitlesWhenVisible
			v-if="globalTitles?.MAIN_POPULAR_MOVIES?.edges?.length"
			type="POPULAR"
			:objectType="ObjectType.Movie"
			:language="language"
			:country="country"
			module-name="MAIN_POPULAR_MOVIES"
			:titles="globalTitles?.MAIN_POPULAR_MOVIES?.edges ?? []"
		/>

		<LazyGlobalTitlesWhenVisible
			v-if="globalTitles?.MAIN_POPULAR_SHOWS?.edges?.length"
			type="POPULAR"
			:objectType="ObjectType.Show"
			:language="language"
			:country="country"
			module-name="MAIN_POPULAR_SHOWS"
			:titles="globalTitles?.MAIN_POPULAR_SHOWS?.edges ?? []"
		/>

		<div v-if="hasSports" class="landing__heading--sub">
			<h2>{{ $t('WEBAPP_GLOBAL_SPORTS_HEADING') }}</h2>
		</div>

		<GlobalSports
			v-if="hasSports"
			:language="language"
			:country="country"
			:webLocale="webLocale"
			module-name="MAIN_TOP_SPORTS"
			:sportCompetitions="globalTitles?.sportCompetitionsV2"
			:sportEventBuckets="globalTitles?.sportEventBucketsV2"
		/>

		<div class="recommendations">
			<div class="jw-logo" style="--aspect-ratio: 1">
				<img :src="logoSrc" loading="lazy" alt="JustWatch LOGO" />
			</div>

			<h2 class="recommendations__text">
				{{ $t('WEBAPP_GLOBAL_BEST_MOVIES_SHOWS') }}
			</h2>

			<LazyCallToAction @click="$emit('scrollToTop')" />
		</div>

		<div class="container countries-list">
			<h2>{{ $t('WEBAPP_GLOBALHOME_COUNTRIES_TITLE') }}</h2>
			<LazyCountriesList />
		</div>

		<LazyAppFooter class="footer-container" />
	</div>
</template>

<script setup lang="ts">
import { ObjectType, Platform } from '@/@types/graphql-types';

import { COUNTRY_EXCEPTIONS } from '@/constants/web-locales.constant';

import {
	GetGlobalHomeDocument,
	GetGlobalHomeQuery,
	GetGlobalHomeQueryVariables,
} from '@/features/global-home/components/graphql/queries/GetGlobalHome.query';
import { isSportSupported } from '@/features/sports/types/enums';
import { HomepageSports } from '@/features/sports/types/sports-types';

import { usePersistedTodaysDate } from '@/helpers/composables/usePersistedTodaysDate';
import { captureMessageForSentry } from '@/libraries/sentry';

import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useStartPageJwtvExperiment, useStartPageJwtvModules } from '@/components/experiments/startPageJwtv';

import { storeToRefs } from 'pinia';
import { useMounted } from '@vueuse/core';
import { computed, toValue } from 'vue';

import GlobalHero from '@/features/global-home/components/GlobalHero.vue';
import GlobalTitles from '@/features/global-home/components/GlobalTitles.vue';

const StartPageHeroRotator = defineAsyncComponent(
	() => import('@/components/experiments/startPageJwtv/StartPageHeroRotator.vue')
);

const GlobalSports = defineAsyncComponent(() => import('@/features/global-home/components/sports/GlobalSports.vue'));

const LazyGlobalTitles = defineLazyHydrationComponent(
	'idle',
	() => import('@/features/global-home/components/GlobalTitles.vue')
);
const LazyGlobalTitlesWhenVisible = defineLazyHydrationComponent(
	'visible',
	() => import('@/features/global-home/components/GlobalTitles.vue')
);

const LazyGlobalCards = defineLazyHydrationComponent(
	'idle',
	() => import('@/features/global-home/components/card/GlobalCards.vue')
);

const LazyGlobalLargeSpotlight = defineLazyHydrationComponent(
	'visible',
	() => import('@/features/global-home/components/GlobalLargeSpotlight.vue')
);

const LazyGlobalSmallSpotlight = defineLazyHydrationComponent(
	'visible',
	() => import('@/features/global-home/components/GlobalSmallSpotlight.vue')
);
const LazyGlobalProviderSpotlight = defineLazyHydrationComponent(
	'visible',
	() => import('@/features/global-home/components/GlobalProviderSpotlight.vue')
);
const LazyCallToAction = defineLazyHydrationComponent(
	'visible',
	() => import('@/features/global-home/components/CallToAction.vue')
);
const LazyCountriesList = defineLazyHydrationComponent(
	'idle',
	() => import('@/components/countries-list/CountriesList.vue')
);
const LazyAppFooter = defineLazyHydrationComponent('visible', () => import('@/components/footer/AppFooter.vue'));

// Hardcoded value for now @liam.best
const spotlightProvider = { shortName: 'atp' };

const logoSrc = `/${ASSETS_DIR}/img/home/logo.svg`;
const v3BgSrc = `/${ASSETS_DIR}/img/home/start-page-jwtv-v3.png`;

const { providersByShortName } = storeToRefs(useConstantStore());
const { country, language, webLocale } = storeToRefs(useLanguageStore());
const { todayDate } = usePersistedTodaysDate();

const isMounted = useMounted();
const { activeVariant: startPageJwtvVariant } = useStartPageJwtvExperiment();
const isV3Active = computed(() => isMounted.value && startPageJwtvVariant.value === 'variant_3');
const isV1Active = computed(() => isMounted.value && startPageJwtvVariant.value === 'variant_1');
const { jwtvModules, jwtvModulesLoading } = useStartPageJwtvModules();

const globalTitlesQuery = useQuery<GetGlobalHomeQuery, GetGlobalHomeQueryVariables>(
	GetGlobalHomeDocument,
	() => {
		return {
			platform: Platform.Web,
			language: toValue(language),
			country: toValue(country),
			dateToday: todayDate.value.toISOString().split('T')[0],
			timezoneOffset: 0,
			competitionFilter: { activeOn: todayDate.value.toISOString() },
			eventFilter: {
				startAt: { from: todayDate.value.toISOString(), to: null },
				competitionIds: [],
				sports: HomepageSports,
			},
			sportBucketSize: 2,
		};
	},
	{ errorPolicy: 'all', fetchPolicy: 'cache-first' }
);

const globalTitles = computed(() => globalTitlesQuery.result.value);
globalTitlesQuery.onError(error => {
	captureMessageForSentry('[GraphQL Get Global Home]:', { error: error, where: 'Component: GlobalHomeNew' }, 'error');
});

const hasSports = computed(() => {
	const isException = COUNTRY_EXCEPTIONS[webLocale.value];
	if (isException) {
		return isSportSupported(isException);
	}

	return isSportSupported(webLocale.value);
});
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.landing {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	background: $jw-color-black;

	@media (max-width: 1050px) {
		transform: translateY(-105px);
	}

	> * {
		margin-top: 125px;

		@media (max-width: 1000px) {
			margin-top: 64px;
		}
	}

	&__featured {
		margin-top: 64px;
	}

	// `.landing` centers its children, so without this the SR collapses to fit-content
	&__sr {
		width: 100%;
	}

	// stays in the DOM when there is no SR so hydration keeps its place
	&__sr:empty {
		display: none;
	}

	&__rotator {
		position: relative;
		z-index: 1;
		width: 85% !important;
		border-radius: 8px;
		overflow: hidden;
		margin-top: 20px;

		// @media (max-width: 750px) {
		// 	width: 100%;
		// 	border-radius: 0;
		// }

		@media (max-width: 550px) {
			width: calc(100% - 10vw) !important;
		}
	}

	&__heading {
		width: 85%;
		margin-bottom: -60px;
		margin-top: 60px !important;

		@media (max-width: 550px) {
			width: 90%;
			margin-bottom: -64px;
			margin-top: 30px !important;
		}

		@media (max-width: 1000px) and (min-width: 550px) {
			margin-bottom: 0px;
		}

		> h2 {
			font-size: 45px;
			font-weight: 900;
			width: 60%;
			text-align: start;
			line-height: 50px;
			margin: 0;

			@media (max-width: 550px) {
				font-size: 28px;
				line-height: 36px;
				margin: 0 0 30px 0;
			}

			@media (max-width: 1000px) {
				width: 100%;
			}
		}

		&--intro {
			width: 85%;
			margin-bottom: -105px;

			@media (max-width: 550px) {
				width: 90%;
				justify-content: center;
				margin-bottom: -88px;
			}

			> h2 {
				font-size: 24px;
				font-weight: 900;
				color: #eaebee;
				width: 60%;
				text-align: start;
				margin: 0;

				@media (max-width: 550px) {
					font-size: 20px;
					text-align: center;
				}

				@media (max-width: 1000px) {
					width: 100%;
				}
			}
		}

		&--sub {
			width: 85%;
			margin-bottom: -105px;

			@media (max-width: 1000px) {
				width: 90%;
				margin-bottom: -40px;
			}

			> h2 {
				font-size: 24px;
				font-weight: 900;
				margin-top: 0;
				margin-bottom: 0;
				margin-inline-end: 20px;
				text-align: start;
				color: #eaebee;

				@media (max-width: 550px) {
					font-size: 20px;
				}
			}
		}
	}
}

.recommendations {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 0 14px;
	max-width: 50em;

	.jw-logo {
		width: 68px;
		height: 68px;
		margin-bottom: 0.6em;

		> img {
			width: 68px;
			height: 68px;
		}
	}

	&__text {
		font-size: 35px;
	}
}

.countries-list {
	h2 {
		text-align: center;
	}
}

.footer-container {
	margin-top: -6em;
}

.header-background {
	position: -webkit-sticky;
	position: sticky;
	top: 0;
	margin-top: 0;
	width: 100%;
	height: 86px;
	background-color: black;
	z-index: 98;

	@media (max-width: 1050px) {
		height: 105px;
	}
}

.hero-section {
	position: relative;
	z-index: 99;
	width: 100%;
	// pull hero up to overlap header-background
	margin-top: -86px;

	@media (max-width: 1050px) {
		margin-top: -105px;
	}

	&--v3 {
		margin-top: 20px;

		@media (max-width: 1050px) {
			margin-top: 105px !important;
		}
	}
}

.v-hidden {
	visibility: hidden;
}
.v3-bg {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 500px;
	z-index: 0;
	overflow: hidden;
	margin-top: 0;
	@media (max-width: 1050px) {
		margin-top: 105px !important;
	}
	img {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center top;
	}

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(180deg, transparent 50%, #000000 100%);
		z-index: 1;
	}
}
</style>
